// Datos del Inicio «Orion». SOLO lecturas a endpoints que ya existen; nada se
// inventa. Si un endpoint no responde, la tarjeta muestra su estado (vacío,
// sin acceso o error) — nunca un número de ejemplo.
//
// Fuente de cada KPI (todas con la org del JWT):
//   · Agentes activos      → GET /django-api/api/v1/admin/assistants/?tab=all   (active=true)
//   · Leads generados      → GET /django-api/api/v1/admin/sales/leads/          (created_at; serie diaria en cliente)
//   · Citas agendadas      → GET /django-api/api/v1/admin/bookings-1to1/?year&month  (start_at, sin canceladas)
//                            + GET /orion/api/calendar/appointments?date_from&date_to (agenda del agente)
//   · Contenido publicado  → GET /django-api/api/v1/admin/posts/?status=published   (published_at)
//   · Oportunidades        → GET /django-api/api/v1/orgscan/reporte-org             (matriz_oportunidades)
//   · Cambios del sitio    → GET /django-api/api/v1/admin/site-config/revisions?status=pending

import { useQuery } from '@tanstack/react-query';

import { djangoApiCall, getMyThreads, getSupervisorAlerts, orionGet, threadDisplayTitle, type SupervisorAlerts } from '@/lib/api';
import { listAssistants } from '@/modules/assistants/api';
import type { AssistantOut } from '@/modules/assistants/types';

export const DAY_MS = 86_400_000;

/** Error con status HTTP (lo adjunta handleResponse de lib/api.ts). */
export function httpStatus(err: unknown): number | undefined {
  const s = (err as { status?: unknown } | null)?.status;
  return typeof s === 'number' ? s : undefined;
}

export interface Period {
  /** inicio del periodo actual */
  from: number;
  /** ahora */
  to: number;
  /** inicio del periodo anterior (mismo largo) */
  prevFrom: number;
  days: number;
}

export function makeWindow(days: number, now = Date.now()): Period {
  // Días civiles completos en hora local: hoy cuenta como el último día.
  const end = new Date(now);
  const startToday = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  const from = startToday - (days - 1) * DAY_MS;
  return { from, to: now, prevFrom: from - days * DAY_MS, days };
}

/** Conteo por día del periodo actual (índice 0 = primer día). */
export function dailySeries(times: number[], w: Period): number[] {
  const out = new Array(w.days).fill(0);
  for (const t of times) {
    if (t < w.from || t > w.to) continue;
    const idx = Math.floor((t - w.from) / DAY_MS);
    if (idx >= 0 && idx < w.days) out[idx] += 1;
  }
  return out;
}

export function periodCounts(times: number[], w: Period): { current: number; previous: number } {
  let current = 0;
  let previous = 0;
  for (const t of times) {
    if (t >= w.from && t <= w.to) current += 1;
    else if (t >= w.prevFrom && t < w.from) previous += 1;
  }
  return { current, previous };
}

function ts(v: string | null | undefined): number | null {
  if (!v) return null;
  const n = Date.parse(v);
  return Number.isFinite(n) ? n : null;
}

// ── Agentes ───────────────────────────────────────────────────

export function useAgents() {
  return useQuery<AssistantOut[]>({
    queryKey: ['orion-shell', 'agents'],
    queryFn: () => listAssistants('all'),
  });
}

// ── Leads ─────────────────────────────────────────────────────

export interface TimesResult {
  times: number[];
  /** true si se llegó al tope de páginas y pudo quedar fuera algo del periodo anterior. */
  truncated: boolean;
}

const LEADS_PAGE = 500;
const LEADS_MAX_PAGES = 4;

export function useLeadTimes(w: Period) {
  return useQuery<TimesResult>({
    queryKey: ['orion-shell', 'leads', w.days, Math.floor(w.from / DAY_MS)],
    queryFn: async () => {
      const times: number[] = [];
      let truncated = false;
      for (let page = 0; page < LEADS_MAX_PAGES; page++) {
        const batch = await djangoApiCall<Array<{ created_at?: string }>>(
          `/admin/sales/leads/?limit=${LEADS_PAGE}&offset=${page * LEADS_PAGE}`,
        );
        const list = Array.isArray(batch) ? batch : [];
        let oldest = Infinity;
        for (const l of list) {
          const t = ts(l.created_at);
          if (t !== null) {
            times.push(t);
            oldest = Math.min(oldest, t);
          }
        }
        // Lista ordenada por -created_at: si ya bajamos del periodo anterior, basta.
        if (list.length < LEADS_PAGE || oldest < w.prevFrom) break;
        if (page === LEADS_MAX_PAGES - 1) truncated = true;
      }
      return { times, truncated };
    },
  });
}

export function useLeadsTotal() {
  return useQuery<number>({
    queryKey: ['orion-shell', 'leads-total'],
    queryFn: async () => (await djangoApiCall<{ total: number }>('/admin/sales/leads/count')).total ?? 0,
  });
}

// ── Citas ─────────────────────────────────────────────────────

export interface Appt {
  id: string;
  title: string;
  start: number;
  end: number | null;
  status: string;
  source: 'reservas' | 'agente';
  who?: string;
}

const CANCELED = new Set(['canceled', 'cancelled', 'no_show', 'noshow']);

function monthsBetween(from: number, to: number): Array<{ y: number; m: number }> {
  const out: Array<{ y: number; m: number }> = [];
  const d = new Date(from);
  d.setDate(1);
  const end = new Date(to);
  while (d.getFullYear() < end.getFullYear() || (d.getFullYear() === end.getFullYear() && d.getMonth() <= end.getMonth())) {
    out.push({ y: d.getFullYear(), m: d.getMonth() + 1 });
    d.setMonth(d.getMonth() + 1);
    if (out.length > 8) break;
  }
  return out;
}

export interface ApptsResult {
  items: Appt[];
  /** fuentes que respondieron (para explicar estados vacíos) */
  sources: { reservas: boolean; agente: boolean };
}

/** Citas desde el inicio del periodo anterior hasta +45 días (para «Próximas»). */
export function useAppointments(w: Period) {
  const horizon = w.to + 45 * DAY_MS;
  return useQuery<ApptsResult>({
    queryKey: ['orion-shell', 'appts', w.days, Math.floor(w.from / DAY_MS)],
    queryFn: async () => {
      const items: Appt[] = [];
      const sources = { reservas: false, agente: false };

      const months = monthsBetween(w.prevFrom, horizon);
      const bookings = await Promise.allSettled(
        months.map(({ y, m }) =>
          djangoApiCall<Array<{ id: number; attendee_name?: string; start_at: string; end_at?: string; status: string }>>(
            `/admin/bookings-1to1/?year=${y}&month=${m}`,
          ),
        ),
      );
      for (const r of bookings) {
        if (r.status !== 'fulfilled' || !Array.isArray(r.value)) continue;
        sources.reservas = true;
        for (const b of r.value) {
          const start = ts(b.start_at);
          if (start === null) continue;
          items.push({
            id: `b-${b.id}`,
            title: b.attendee_name || 'Reserva',
            who: b.attendee_name || undefined,
            start,
            end: ts(b.end_at),
            status: b.status,
            source: 'reservas',
          });
        }
      }

      try {
        const q = new URLSearchParams({
          date_from: new Date(w.prevFrom).toISOString(),
          date_to: new Date(horizon).toISOString(),
        });
        const appts = await orionGet<Array<{ id?: string; title?: string; start_at: string; end_at?: string; status?: string }>>(
          `/calendar/appointments?${q}`,
        );
        if (Array.isArray(appts)) {
          sources.agente = true;
          for (const a of appts) {
            const start = ts(a.start_at);
            if (start === null) continue;
            items.push({
              id: `a-${a.id ?? start}`,
              title: a.title || 'Cita',
              start,
              end: ts(a.end_at),
              status: a.status || 'pending',
              source: 'agente',
            });
          }
        }
      } catch {
        /* agenda del agente no disponible → solo reservas */
      }

      items.sort((a, b) => a.start - b.start);
      return { items, sources };
    },
  });
}

export function isActiveAppt(a: Appt): boolean {
  return !CANCELED.has(String(a.status || '').toLowerCase());
}

// ── Contenido ─────────────────────────────────────────────────

export type PostStatusKey = 'published' | 'scheduled' | 'draft' | 'failed';

export function usePostStatusCounts(enabled = true) {
  return useQuery<Record<PostStatusKey, number>>({
    enabled,
    queryKey: ['orion-shell', 'post-counts'],
    queryFn: async () => {
      const keys: PostStatusKey[] = ['published', 'scheduled', 'draft', 'failed'];
      const res = await Promise.all(
        keys.map((k) => djangoApiCall<{ count: number }>(`/admin/posts/?status=${k}&limit=1`)),
      );
      return Object.fromEntries(keys.map((k, i) => [k, res[i]?.count ?? 0])) as Record<PostStatusKey, number>;
    },
  });
}

const POSTS_PAGE = 100;
const POSTS_MAX_PAGES = 3;

export function usePublishedTimes(w: Period) {
  return useQuery<TimesResult>({
    queryKey: ['orion-shell', 'published', w.days, Math.floor(w.from / DAY_MS)],
    queryFn: async () => {
      const times: number[] = [];
      let truncated = false;
      for (let page = 0; page < POSTS_MAX_PAGES; page++) {
        const r = await djangoApiCall<{ items: Array<{ published_at: string | null; updated_at?: string }>; count: number }>(
          `/admin/posts/?status=published&limit=${POSTS_PAGE}&offset=${page * POSTS_PAGE}`,
        );
        const list = r?.items ?? [];
        for (const p of list) {
          const t = ts(p.published_at);
          if (t !== null) times.push(t);
        }
        const fetched = (page + 1) * POSTS_PAGE;
        if (list.length < POSTS_PAGE || fetched >= (r?.count ?? 0)) break;
        if (page === POSTS_MAX_PAGES - 1) truncated = true;
      }
      return { times, truncated };
    },
  });
}

// ── Diagnóstico organizacional ────────────────────────────────

export interface ReporteOrg {
  scan_id: string;
  participantes: number;
  listo: boolean;
  faltan_participantes: number;
  matriz_oportunidades: Array<Record<string, unknown>>;
}

export type ReporteState =
  | { kind: 'none' }            // la org no tiene diagnóstico (404)
  | { kind: 'forbidden' }       // sin permiso / módulo apagado (403)
  | { kind: 'report'; data: ReporteOrg };

export function useReporteOrg() {
  return useQuery<ReporteState>({
    queryKey: ['orion-shell', 'reporte-org'],
    queryFn: async () => {
      try {
        const data = await djangoApiCall<ReporteOrg>('/orgscan/reporte-org');
        return { kind: 'report', data };
      } catch (e) {
        const s = httpStatus(e);
        if (s === 404) return { kind: 'none' };
        if (s === 403) return { kind: 'forbidden' };
        throw e;
      }
    },
    retry: false,
  });
}

// ── Sitio ─────────────────────────────────────────────────────

export interface RevisionDiff {
  path: string;
  before?: unknown;
  after?: unknown;
}

export interface Revision {
  id: number;
  page_slug: string;
  section_key: string;
  op: string;
  status: string;
  summary: string;
  reason?: string;
  diff: RevisionDiff[];
  actor?: string;
  actor_kind: string;
  created_at?: string | null;
  applied_at?: string | null;
  applied_by?: string;
  applied_by_kind?: string;
  reverts_id?: number | null;
}

export interface RevisionDetail extends Revision {
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
}

/** 🛎️ Alertas del supervisor para la campana. Se refresca cada minuto. */
export function useSupervisorAlerts(enabled = true) {
  return useQuery<SupervisorAlerts | null>({
    enabled,
    queryKey: ['orion-shell', 'supervisor-alertas'],
    queryFn: getSupervisorAlerts,
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useRevisions(status: string | null, limit = 100, enabled = true) {
  return useQuery<Revision[]>({
    enabled,
    queryKey: ['orion-shell', 'revisions', status ?? 'all', limit],
    queryFn: () =>
      djangoApiCall<Revision[]>(
        `/admin/site-config/revisions?limit=${limit}${status ? `&status=${encodeURIComponent(status)}` : ''}`,
      ),
  });
}

export function useRevisionDetail(id: number | null) {
  return useQuery<RevisionDetail>({
    queryKey: ['orion-shell', 'revision', id],
    queryFn: () => djangoApiCall<RevisionDetail>(`/admin/site-config/revisions/${id}`),
    enabled: id !== null,
  });
}

export function useEditPolicy() {
  return useQuery<{ edit_mode: string; changelog_public: boolean }>({
    queryKey: ['orion-shell', 'edit-policy'],
    queryFn: () => djangoApiCall('/admin/site-config/edit-policy'),
  });
}

export interface OrgDomain {
  domain: string;
  kind: string;
  is_primary: boolean;
  is_expired: boolean;
  is_resolvable: boolean;
}

/** Dominio público principal de la org (para la vista previa del sitio). */
export function usePrimaryDomain() {
  return useQuery<string | null>({
    queryKey: ['orion-shell', 'domain'],
    queryFn: async () => {
      const list = await djangoApiCall<OrgDomain[]>('/admin/site-config/domains');
      const ok = (Array.isArray(list) ? list : []).filter((d) => !d.is_expired && d.is_resolvable !== false);
      const pick =
        ok.find((d) => d.is_primary && d.kind !== 'preview') ||
        ok.find((d) => d.kind !== 'preview') ||
        null;
      return pick ? pick.domain : null;
    },
  });
}

// ── Identidad ─────────────────────────────────────────────────

export function useCompanyName() {
  return useQuery<string | null>({
    queryKey: ['orion-shell', 'company'],
    queryFn: async () => (await djangoApiCall<{ name?: string }>('/admin/company/'))?.name || null,
    staleTime: 10 * 60_000,
  });
}

export function useMyName(userId: string) {
  return useQuery<string | null>({
    queryKey: ['orion-shell', 'me', userId],
    queryFn: async () => {
      const users = await orionGet<Array<{ user_id: string; full_name?: string | null; username?: string }>>('/org/users');
      const me = (Array.isArray(users) ? users : []).find((u) => u.user_id === userId);
      return me?.full_name || null;
    },
    enabled: !!userId,
    staleTime: 10 * 60_000,
  });
}

export function useMyThreads() {
  return useQuery<Array<{ id: string; title: string; hasTitle: boolean; at: string | null }>>({
    queryKey: ['orion-shell', 'threads'],
    queryFn: async () => {
      const data = await getMyThreads();
      return (data?.threads || []).map((t: any) => ({
        id: String(t.id || t.thread_id || t.session_id),
        title: threadDisplayTitle(t),
        hasTitle: !!t.title,
        at: t.last_message_at || null,
      }));
    },
  });
}

// ── Portada del sitio (para la vista previa estática) ────────

export interface SiteHero {
  title: string | null;
  image: string | null;
}

function firstImage(cfg: Record<string, any> | undefined): string | null {
  if (!cfg) return null;
  const slides = Array.isArray(cfg.reel_slides) ? cfg.reel_slides : [];
  const candidates = [
    slides[0]?.image,
    cfg.image,
    cfg.background_image,
    cfg.bg_image,
    cfg.media?.image,
  ];
  const hit = candidates.find((v) => typeof v === 'string' && /^https:\/\//.test(v));
  return hit || null;
}

/** Título e imagen reales del hero de la portada (GET /admin/site-config/). */
export function useSiteHero() {
  return useQuery<SiteHero>({
    queryKey: ['orion-shell', 'site-hero'],
    queryFn: async () => {
      const cfg = await djangoApiCall<{ sections?: Array<{ key: string; page_slug?: string; is_enabled?: boolean; order?: number; config?: Record<string, any> }> }>(
        '/admin/site-config/',
      );
      const home = (cfg?.sections ?? [])
        .filter((s) => (s.page_slug ?? '') === '' && s.is_enabled !== false && /^hero/.test(s.key))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
      return {
        title: typeof home?.config?.title === 'string' ? home.config.title : null,
        image: firstImage(home?.config),
      };
    },
    staleTime: 10 * 60_000,
  });
}
